<script setup>
import { ref, onMounted, computed } from "vue";
import api from "@/services/apiService";
import { useRouter } from "vue-router";

import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import BackendPagination from "@/components/BackendPagination.vue";

const agents = ref([]);
const errorMessage = ref("");
const successMessage = ref("");
const loading = ref(true);
const router = useRouter();
const showModal = ref(false);
const isEditing = ref(false);
const selectedAgent = ref(null);

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);

const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage)
);

const fetchAgents = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/users?page=${currentPage.value}&itemsPerPage=${itemsPerPage}`);
    agents.value = response.data.member || [];
    totalItems.value = response.data.totalItems || 0;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "Error al obtener la lista de agentes.";
  } finally {
    loading.value = false;
  }
};

// Pagination handler
const changePage = async (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    await fetchAgents();
  }
};

const toggleApproval = async (agent) => {
  const agentId = agent["@id"].replace(/^\/api/, "");
  const newStatus = !agent.approved;

  try {
    await api.patch(`${agentId}`, { approved: newStatus });
    agent.approved = newStatus;
  } catch (error) {
    console.error("❌ Error al actualizar la aprobación:", error);
  }
};

const openModal = () => {
  isEditing.value = false;
  selectedAgent.value = {
    fullName: "",
    email: "",
    password: "",
    approved: false
  };
  showModal.value = true;
};

const openEditModal = (agent) => {
  isEditing.value = true;
  selectedAgent.value = {
    "@id": agent["@id"],
    fullName: agent.fullName || "",
    email: agent.email || "",
    approved: agent.approved === true,
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedAgent.value = null;
  errorMessage.value = "";
};

const saveUser = async () => {
  try {
    if (isEditing.value) {
      const agentId = selectedAgent.value["@id"].replace(/^\/api/, "");
      const payload = {
        fullName: selectedAgent.value.fullName,
        email: selectedAgent.value.email,
        approved: Boolean(selectedAgent.value.approved),
      };

      const response = await api.patch(agentId, payload);

      const index = agents.value.findIndex(a => a["@id"] === selectedAgent.value["@id"]);
      if (index !== -1) {
        agents.value[index] = { ...agents.value[index], ...response.data };
      }

      successMessage.value = `Agente "${selectedAgent.value.fullName}" actualizado correctamente.`;
    } else {
      const newUser = {
        fullName: selectedAgent.value.fullName,
        email: selectedAgent.value.email,
        password: selectedAgent.value.password,
        approved: Boolean(selectedAgent.value.approved),
      };

      if (!newUser.fullName || !newUser.email || !newUser.password) {
        errorMessage.value = "Todos los campos son obligatorios.";
        return;
      }

      await api.post("/register", newUser);

      successMessage.value = `Agente "${newUser.fullName}" agregado correctamente.`;
    }

    await fetchAgents();
    closeModal();

    setTimeout(() => {
      successMessage.value = "";
      errorMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al guardar usuario:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al guardar el usuario.";
  }
};

const deleteUser = async (agent) => {
  if (!confirm(`¿Estás seguro de que quieres eliminar a ${agent.fullName}?`)) return;

  try {
    const agentId = agent["@id"].replace(/^\/api/, "");
    await api.delete(agentId);

    successMessage.value = `Agente "${agent.fullName}" eliminado correctamente.`;

    await fetchAgents();

    setTimeout(() => {
      successMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al eliminar usuario:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al eliminar el agente.";
  }
};

onMounted(() => {
  const token = localStorage.getItem("auth_token");
  if (!token) {
    router.push("/signin");
  } else {
    fetchAgents();
  }
});
</script>



<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex">
            <h6>Tabla de Agentes</h6>
            <argon-button color="primary" size="sm" class="ms-auto" @click="openModal">
              <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
              Agregar Agente
            </argon-button>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <div v-if="loading" class="text-center py-3">
                <p class="text-secondary">Cargando agentes...</p>
              </div>

              <argon-alert v-if="successMessage" color="alert-success " icon="ni ni-check-bold" class="mb-2 text-dark w-50 ms-3">
                {{ successMessage }}
              </argon-alert>
    
              <argon-alert v-if="errorMessage && !showModal" color="alert-danger" icon="ni ni-fat-remove" class="mb-2 text-dark w-50 ms-3">
                {{ errorMessage }}
              </argon-alert>

              <div v-if="agents.length === 0 && !loading && !errorMessage" class="text-center py-3">
                <img src="@/assets/img/illustrations/noData.png" alt="No hay datos disponibles" width="300">
                <p class="text-secondary">No se encontraron agentes</p>
              </div>

              <table v-if="agents.length > 0" class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Agente</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Correo
                      Electrónico</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Aprobado</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="agent in agents" :key="agent['@id']">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ agent.fullName }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs text-secondary mb-0">{{ agent.email }}</p>
                    </td>
                    <td>
                      <argon-switch :id="'approve-' + agent['@id']" :name="'approve-' + agent['@id']"
                        :checked="agent.approved" @change="toggleApproval(agent)">
                      </argon-switch>
                    </td>

                    <td>
                      <font-awesome-icon :icon="['fas', 'pen-to-square']" class="me-3" @click="openEditModal(agent)"
                        style="cursor:pointer;" />
                      <font-awesome-icon :icon="['fas', 'user-minus']" @click="deleteUser(agent)"
                        style="cursor:pointer;" />
                    </td>


                  </tr>
                </tbody>
              </table>
              <BackendPagination
  :current-page="currentPage"
  :total-pages="totalPages"
  @change-page="changePage"
/>
            </div>
          </div>
        </div>

        <!-- Modal (Fixed Properly) -->
        <teleport to="body">
          <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
              <h5 class="mb-3">{{ isEditing ? "Editar Agente" : "Agregar Nuevo Agente" }}</h5>
              <argon-alert v-if="errorMessage && !showModal" color="alert-danger" icon="ni ni-fat-remove" class="mb-2 text-dark w-50 ms-3">
                {{ errorMessage }}
              </argon-alert>

              <div class="mb-3">
                <label>Nombre Completo</label>
                <input v-model="selectedAgent.fullName" class="form-control" type="text" required />
              </div>
              <div class="mb-3">
                <label>Correo Electrónico</label>
                <input v-model="selectedAgent.email" class="form-control" type="email" required />
              </div>
              <div class="mb-3" v-if="!isEditing">
                <label>Contraseña</label>
                <input v-model="selectedAgent.password" class="form-control" type="password" required />
              </div>
              <div class="mb-3">
                <label>Aprobado</label>
                <argon-switch :id="'modal-approve-switch'" :name="'modal-approve-switch'"
                  :checked="selectedAgent.approved" @change="selectedAgent.approved = !selectedAgent.approved">
                </argon-switch>
              </div>
              <div class="d-flex justify-content-end">
                <argon-button color="secondary" class="me-2" @click="closeModal">Cancelar</argon-button>
                <argon-button color="primary" @click="saveUser">
                  {{ isEditing ? "Guardar Cambios" : "Agregar" }}
                </argon-button>
              </div>
            </div>
          </div>
        </teleport>



      </div>
    </div>
  </div>
</template>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

/* Modal Box */
/* Modal Box - Reduced Width */
.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1051;
  width: 500px;
  /* Reduced width */
  max-width: 90%;
  /* Keeps it responsive */
  position: relative;
}
</style>
